import { lens } from "@dhmk/zustand-lens";
import type { FileUIPart } from "ai";

// ============================================================================
// Addon Type Definitions
// ============================================================================

interface BaseAddonProps {
  removable: boolean;
  singleton?: boolean;
  hidden?: boolean;
}

type BaseAddon = {
  id: string;
  type: PromptAddonType;
};

export enum PromptAddonType {
  ATTACHMENT = "attachment",
  PREVIEW_ROUTE = "preview_route",
  SELECTED_ELEMENT = "selected_element",
  // Add more types here as needed:
  // CODE_SNIPPET = "code_snippet",
}

export enum AttachmentVariant {
  IMAGE = "image",
  FILE = "file",
}

// Define each addon type as an interface extending BaseAddonProps
export interface AttachmentPromptAddon extends BaseAddon {
  type: PromptAddonType.ATTACHMENT;
  props: BaseAddonProps & {
    url: FileUIPart["url"];
    variant: AttachmentVariant;
    mediaType: FileUIPart["mediaType"];
    filename: FileUIPart["filename"];
    uploadStatus: "uploading" | "success";
  };
}

export interface PreviewRoutePromptAddon extends BaseAddon {
  type: PromptAddonType.PREVIEW_ROUTE;
  props: BaseAddonProps & {
    path: string;
  };
}

export interface SelectedElementPromptAddon extends BaseAddon {
  type: PromptAddonType.SELECTED_ELEMENT;
  props: BaseAddonProps & {
    xpath: string;
    uniqueId: string;
    pathname: string;
    previewText?: string;
    tagName?: string;
    elementId?: string;
    className?: string;
  };
}

// Discriminated union of all addon types
export type PromptAddon =
  | AttachmentPromptAddon
  | PreviewRoutePromptAddon
  | SelectedElementPromptAddon;

// Helper type to extract props by type
export type PropsForType<T extends PromptAddonType> = Extract<
  PromptAddon,
  { type: T }
>["props"];

// ============================================================================
// Store
// ============================================================================

type PromptContextState = {
  addons: Array<PromptAddon>;
};

type PromptContextActions = {
  addAddon: <T extends PromptAddonType>(
    type: T,
    props: PropsForType<T>,
  ) => string; // Returns the generated ID

  removeAddon: (id: string) => void;

  updateAddon: <T extends PromptAddonType>(
    id: string,
    props: Partial<PropsForType<T>>,
  ) => void;

  clearAddons: () => void;

  clearAddonsByType: <T extends PromptAddonType>(type: T) => void;

  getAddonsByType: <T extends PromptAddonType>(
    type: T,
  ) => Array<Extract<PromptAddon, { type: T }>>;

  reset: () => void;
};

const defaultPromptContextState: PromptContextState = {
  addons: [],
};

export type PromptContextSlice = PromptContextState & PromptContextActions;

export const promptContextSlice = lens<PromptContextSlice>((set, get, api) => ({
  ...defaultPromptContextState,
  addAddon: (type, props) => {
    const existingAddon = get().addons.find((addon) => addon.type === type);
    if (existingAddon && props.singleton) {
      get().updateAddon(existingAddon.id, props);
      return existingAddon.id;
    }

    const newAddon = { id: crypto.randomUUID(), type, props } as PromptAddon;
    set((state) => ({
      addons: [...state.addons, newAddon],
    }));
    return newAddon.id;
  },

  removeAddon: (id) =>
    set((state) => ({
      addons: state.addons.filter((addon) => addon.id !== id),
    })),

  updateAddon: (id, props) =>
    set((state) => ({
      addons: state.addons.map((addon) =>
        addon.id === id
          ? ({ ...addon, props: { ...addon.props, ...props } } as PromptAddon)
          : addon,
      ),
    })),

  clearAddons: () => set({ addons: [] }),

  clearAddonsByType: (type) =>
    set((state) => ({
      addons: state.addons.filter((addon) => addon.type !== type),
    })),

  getAddonsByType: (type) => {
    return get().addons.filter((addon) => addon.type === type) as Array<
      Extract<PromptAddon, { type: typeof type }>
    >;
  },

  reset: () => {
    set(defaultPromptContextState);
  },
}));
